import * as yup from 'yup'
import moment from 'moment'
// import { Trans } from 'react-i18next'
import i18next from 'i18next'

export const STRING_INPUT = 'string'
export const DATE_INPUT = 'date'
export const NUMBER_INPUT = 'number'

const dateStringToObject = (string) => {
    if (string === 'today') {
        return moment().startOf('day').toDate()
    }
    return undefined
}

export function isValidCuit(cuit) {
    const strCuit = String(cuit)

    if (strCuit === '') {
        return true
    }

    if (strCuit.length !== 11) {
        return false
    }

    const vd = Number(strCuit.slice(-1))

    const verif = strCuit
        .substring(0, 10)
        .split('')
        .reverse()
        .reduce((acc, n, i) => acc + Number(n) * (2 + (i % 6)), 0)

    const cvd = (11 - (verif % 11)) % 11

    return cvd === vd
}

// const formatDate = (currentDate, format) => {
//     return moment(currentDate).format(format)
// }

/**
 * Crea un objeto Map con las reglas de validación.
 * Esto permite manipularlas: rules.has('required') o rules.get('max') devuelve el número o la fecha parseada
 * */
export const parseValidationRules = (validationRulesString) => {
    if (!validationRulesString) {
        return null
    }

    // Si es un objeto, lo convertimos a un Map directamente
    if (typeof validationRulesString === 'object') {
        const validationMap = new Map()
        
        if (validationRulesString.required !== undefined) {
            validationMap.set('required', validationRulesString.required)
        }
        
        if (validationRulesString.min !== undefined) {
            // Convertir el valor mínimo a número, removiendo cualquier formato de moneda
            const minValue = typeof validationRulesString.min === 'string' 
                ? Number(validationRulesString.min.replace(/[^0-9]/g, ''))
                : validationRulesString.min
            validationMap.set('min', minValue)
        }
        
        if (validationRulesString.max !== undefined) {
            // Convertir el valor máximo a número, removiendo cualquier formato de moneda
            const maxValue = typeof validationRulesString.max === 'string'
                ? Number(validationRulesString.max.replace(/[^0-9]/g, ''))
                : validationRulesString.max
            validationMap.set('max', maxValue)
        }
        
        if (validationRulesString.message) {
            validationMap.set('message', validationRulesString.message)
        }
        
        return validationMap
    }

    // Si es una cadena, la procesamos como antes
    return validationRulesString
        .split('|')
        .filter((rule) => rule.trim() !== '')
        .reduce((validationMap, rule) => {
            // "validationRules": "required"
            if (rule === 'required') {
                validationMap.set('required', true)
                return validationMap
            }

            if (rule === 'novalidation') {
                validationMap.set('required', false)
                return validationMap
            }

            if (rule === 'email') {
                validationMap.set('email', true)
                return validationMap
            }

            if (rule === 'dni') {
                validationMap.set('dni', true)
                return validationMap
            }

            if (rule === 'cuit') {
                validationMap.set('cuit', true)
                return validationMap
            }

            // "validationRules": "min:now" || "validationRules": "min:255"
            const minMatches = rule.match(/min:(\w+)/)
            if (minMatches) {
                let min = Number(minMatches[1])
                // eslint-disable-next-line no-restricted-globals
                if (isNaN(minMatches[1])) {
                    min = dateStringToObject(minMatches[1])
                }

                validationMap.set('min', min)

                return validationMap
            }

            // "validationRules": "max:now" || "validationRules": "max:255"
            const maxMatches = rule.match(/max:(\w+)/)
            if (maxMatches) {
                let max = Number(maxMatches[1])
                // eslint-disable-next-line no-restricted-globals
                if (isNaN(maxMatches[1])) {
                    max = dateStringToObject(maxMatches[1])
                }
                validationMap.set('max', max)

                return validationMap
            }

            return validationMap
        }, new Map())
}

// const getTextWithTranslation = (type, a) => {
//     const keyToUse = type === 'min' ? `common:field_date_min` : `common:field_date_max`
//     const date = formatDate(a, i18next.t('common:humanDateYearFormat'))
//     return (
//         <Trans i18nKey={keyToUse} key={keyToUse}>
//             {{ date }}
//         </Trans>
//     )
// }

export const createSchemaFromRules = (rules, dataType = STRING_INPUT) => {
    let validation = yup.string()
    // let isInputDate = false

    if (dataType === DATE_INPUT) {
        validation = yup.date()
        // isInputDate = true
    }

    if (dataType === NUMBER_INPUT) {
        validation = yup.number()
            .transform((value) => {
                if (value === '' || value === null || value === undefined) return null
                // Remover el símbolo de moneda y cualquier otro carácter no numérico
                let numericValue = value
                if (typeof value === 'string') {
                    // Remover $, comas y puntos, dejando solo números
                    numericValue = value.replace(/[^0-9]/g, '')
                }
                const number = Number(numericValue)
                return Number.isNaN(number) ? null : number
            })
            .typeError('Debe ser un número válido')
            .nullable()
    }

    if (rules) {
        // Primero aplicamos las validaciones de tipo
        if (rules.get('required')) {
            validation = validation.required(i18next.t('field_required'))
        }

        if (rules.get('requiredSchema')) {
            validation = validation.required(i18next.t('field_required'))
        }

        if (rules.get('novalidation')) {
            validation = validation.notRequired()
        }

        if (rules.get('email')) {
            validation = validation.email(i18next.t('field_email'))
        }

        if (rules.get('dni')) {
            validation = validation.test(
                'is-valid-dni',
                i18next.t('invalid_dni'),
                (value) => {
                    if (value === null || value === undefined || value === '') return true
                    const numericValue = String(value).replace(/\./g, '')
                    return /^\d{7,8}$/.test(numericValue)
                }
            )
        }

        if (rules.get('cuit')) {
            validation = validation.test(
                'cuit_valid',
                i18next.t('invalid_cuit'),
                (value) => {
                    if (!value || value === '') return true
                    const cleanValue = String(value).replace(/\./g, '').replace(/-/g, '')
                    return isValidCuit(cleanValue)
                }
            )
        }

        // Luego aplicamos las validaciones de rango
        if (rules.get('min') !== undefined) {
            const minValue = rules.get('min')
            const messages = rules.get('message')
            const minMessage = messages?.min || i18next.t('field_min', { min: minValue })
            
            // Agregar test personalizado para min
            validation = validation.test('min', minMessage, function testMin(value) {
                if (value === null || value === undefined) return true
                return value >= minValue
            })
        }

        if (rules.get('max') !== undefined) {
            const maxValue = rules.get('max')
            const messages = rules.get('message')
            const maxMessage = messages?.max || i18next.t('field_max', { max: maxValue })
            
            // Agregar test personalizado para max
            validation = validation.test('max', maxMessage, function testMax(value) {
                if (value === null || value === undefined) return true
                return value <= maxValue
            })
        }
    }

    return validation
}

export const countPartErrors = (part, inputs) => {
    const input = inputs.get(part.id)
    let errors = 0

    if (part.parts) {
        // Si tiene sub partes, las recorro

        part.parts.forEach((subPart) => {
            errors += countPartErrors(subPart, inputs)
        })
    }

    if (input) {
        // Si tiene un store de input, me fijo si es válido
        if (input.isValid) {
            return 0
        }

        return 1
    }

    return errors
}

export const validatePart = (part, inputs) => {
    const input = inputs.get(part.id)

    if (part.parts) {
        // Si tiene sub partes, las recorro

        part.parts.forEach((subPart) => {
            validatePart(subPart, inputs)
        })
    } else if (input) {
        input.validate()
    }
}

export const getPartErrorMessages = (part, inputs) => {
    const input = inputs.get(part.id)

    if (input) {
        if (!input.isValid) {
            return [
                {
                    label: part.label,
                    message: input.errorMessage || input.store.errorMessage,
                },
            ]
        }
    }

    let errors = []
    if (part.parts) {
        // Si tiene sub partes, las recorro
        part.parts.forEach((subPart) => {
            errors = [...errors, ...getPartErrorMessages(subPart, inputs)]
        })
    }

    return errors
}

export const partIsValid = (part, inputs) => {
    return !countPartErrors(part, inputs)
}

function verifyCBU(block = '', bm = []) {
    const blockList = block.split('')
    const blockVd = Number(blockList.pop())

    const blockCalc = blockList.reduce((acc, n, i) => acc + Number(n) * bm[i], 0)

    const blockCalcLastDigit = Number(blockCalc.toString().slice(-1))

    const verifDigit = 10 - blockCalcLastDigit

    if (blockVd === 0 && verifDigit === 10) {
        return true
    }

    if (blockVd === verifDigit) {
        return true
    }

    return false
}

export function isValidCBU(cbu) {
    if (cbu == null || cbu === '') {
        return true
    }

    if (typeof cbu !== 'string' && typeof cbu !== 'bigint') {
        throw new TypeError('The CBU parameter must be a string or BigInt.')
    }

    const strCBU = cbu.toString()

    if (strCBU.length !== 22) {
        return false
    }

    const bankBlock = strCBU.slice(0, 8)

    if (!verifyCBU(bankBlock, [7, 1, 3, 9, 7, 1, 3])) {
        return false
    }

    const accountBlock = strCBU.slice(8)

    if (!verifyCBU(accountBlock, [3, 9, 7, 1, 3, 9, 7, 1, 3, 9, 7, 1, 3])) {
        return false
    }

    return true
}

function checkLuhn(val = '') {
    const valList = val.split('')

    const parity = valList.length % 2

    const sumDigit = Number(valList.pop())

    const sum = valList.reduce((acc, d, i) => {
        let digit = Number(d)

        if (i % 2 === parity) {
            digit *= 2
        }

        if (digit > 9) {
            digit -= 9
        }

        return acc + digit
    }, sumDigit)

    return sum % 10 === 0
}

export function isValidCC(cc) {
    if (cc == null || cc === '') {
        return true
    }

    if (typeof cc !== 'string' && typeof cc !== 'number') {
        throw new TypeError('The Credit Card parameter must be a string or number.')
    }

    const strCC = cc.toString().replace(/\D+/g, '')

    if (strCC.length < 13 || strCC.length > 19) {
        return false
    }

    return checkLuhn(strCC)
}

export default createSchemaFromRules

export function objectIsEmpty(obj) {
    return obj && Object.keys(obj).length === 0 && obj.constructor === Object
}
